import type { ComponentProps, ReactNode } from 'react'
import { useId } from 'react'
import { uiScope } from '@/lib/cssScope'
import styles from '@/styles/components/ui/ProblemCard.module.css'

/** Value color: assets (neutral) or severity band. */
export type ProblemTone = 'neutral' | 'critical' | 'high' | 'medium' | 'low'

export interface ProblemCardSplit {
  left: { label: string; value: ReactNode }
  /** Problems count: use red accent for the value by default */
  right: { label: string; value: ReactNode; valueAccent?: 'problems' }
}

export interface ProblemCardColumn {
  label: string
  value: ReactNode
  valueAccent?: 'problems'
}

export interface ProblemCardProps extends Omit<ComponentProps<'div'>, 'title'> {
  title?: string
  value?: ReactNode
  /** Wide summary: two columns (e.g. assets total | problems). When set, title/value stack is not used. */
  split?: ProblemCardSplit
  /** Wide summary: three or more equal columns (e.g. IS | assets | problems). */
  columns?: ProblemCardColumn[]
  /** Secondary line under column layout (e.g. problem breakdown). */
  subline?: ReactNode
  footer?: ReactNode
  tone?: ProblemTone
  hoverDetail?: ReactNode
}

const toneRoot: Record<ProblemTone, string> = {
  neutral: styles.toneNeutral,
  critical: styles.toneCritical,
  high: styles.toneHigh,
  medium: styles.toneMedium,
  low: styles.toneLow,
}

/** Metrics card: neutral row or severity-tinted tile. Root: `ui-problem-card`. */
export function ProblemCard({
  title,
  value,
  split,
  columns,
  subline,
  footer,
  tone = 'neutral',
  hoverDetail,
  className = '',
  ...rest
}: ProblemCardProps) {
  const tipId = useId()
  const describedBy = hoverDetail ? tipId : undefined
  const severityLayout = tone !== 'neutral' && !split && !columns

  const root = [
    uiScope('problem-card'),
    styles.root,
    toneRoot[tone],
    split || columns ? styles.rootSplit : '',
    hoverDetail ? styles.hoverHost : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const titleClass = [
    styles.title,
    severityLayout ? styles.titleSeverity : styles.titleMuted,
    'ui-problem-card__title',
  ].join(' ')

  const valueClass = [
    styles.value,
    severityLayout ? styles.valuePlain : styles.valueNeutral,
    'ui-problem-card__value',
  ].join(' ')

  const body = columns?.length ? (
    <>
      <div className={`${styles.split} ${styles.splitTriple} ui-problem-card__split`}>
        {columns.map((col) => (
          <div key={col.label} className={`${styles.splitCol} ui-problem-card__split-col`}>
            <p
              className={`${styles.splitValue} ${
                col.valueAccent === 'problems' ? styles.splitValueProblems : ''
              } ui-problem-card__split-value`}
            >
              {col.value}
            </p>
            <p className={`${styles.splitLabel} ui-problem-card__split-label`}>{col.label}</p>
          </div>
        ))}
      </div>
      {subline ? <p className={`${styles.subline} ui-problem-card__subline`}>{subline}</p> : null}
    </>
  ) : split ? (
    <div className={`${styles.split} ui-problem-card__split`}>
      <div className={`${styles.splitCol} ui-problem-card__split-col`}>
        <p className={`${styles.splitValue} ui-problem-card__split-value`}>{split.left.value}</p>
        <p className={`${styles.splitLabel} ui-problem-card__split-label`}>{split.left.label}</p>
      </div>
      <div className={`${styles.splitCol} ui-problem-card__split-col`}>
        <p
          className={`${styles.splitValue} ${
            split.right.valueAccent === 'problems' ? styles.splitValueProblems : ''
          } ui-problem-card__split-value`}
        >
          {split.right.value}
        </p>
        <p className={`${styles.splitLabel} ui-problem-card__split-label`}>{split.right.label}</p>
      </div>
    </div>
  ) : (
    <>
      {title !== undefined ? <p className={titleClass}>{title}</p> : null}
      {value !== undefined ? <p className={valueClass}>{value}</p> : null}
    </>
  )

  return (
    <div
      className={root}
      tabIndex={hoverDetail ? 0 : undefined}
      aria-describedby={describedBy}
      {...rest}
    >
      {body}
      {footer !== undefined ? (
        <p className={`${styles.footer} ui-problem-card__footer`}>{footer}</p>
      ) : null}
      {hoverDetail ? (
        <div id={tipId} className={`${styles.tooltip} ui-problem-card__tooltip`} role="tooltip">
          {hoverDetail}
        </div>
      ) : null}
    </div>
  )
}

export default ProblemCard
